import DateDisplay from '@app/src/components/DateDisplay'
import AssessmentScoreGauge from '@app/src/components/Ui/AssessmentScoreGauge'
import TruncateWithDialog from '@app/src/components/Ui/TruncateWithDialog'
import type Assessment from '@app/src/types/assessment'
import type { Provider } from '@app/src/types/organizations'
import { AssessmentType } from '@app/src/types/resourceExplorer'
import { Box, Chip, Divider, Paper, Stack, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import AssessmentLocationAndRisks from './AssessmentLocationAndRisks'
import AssessmentSectionRatings from './AssessmentSectionRatings'
import AssessmentStatusChip from './AssessmentStatusChip'
import { getAssessmentLevelTexts } from './assessmentSummaryLogic'
import { getTemplateSections } from './assessmentAnswersLogic'
import { useProviderRiskIndices } from './useProviderRiskIndices'

interface AssessmentSummaryCardProps {
  assessment: Assessment
  provider: Provider | undefined
  showSourceChip: boolean
  showLocationAndRisks: boolean
}

const AssessmentSummaryCard: React.FC<AssessmentSummaryCardProps> = ({
  assessment,
  provider,
  showSourceChip,
  showLocationAndRisks,
}) => {
  const { formatMessage } = useIntl()
  const template = assessment.assessmentTemplate
  const isBaseline = template.assessmentType === AssessmentType.BaselineAssessment
  const { label, thresholdText, description } = getAssessmentLevelTexts(assessment, provider?.name, formatMessage)
  const { riskIndices } = useProviderRiskIndices(provider?.id, assessment.assessmentTemplateId, showLocationAndRisks)
  const hasSectionRatings = getTemplateSections(assessment).length > 0

  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <Stack spacing={3} divider={<Divider flexItem />}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ xs: 'flex-start', sm: 'center' }}>
          <Stack alignItems="center">
            <AssessmentStatusChip assessment={assessment} />
            <AssessmentScoreGauge level={assessment.totalLevel ?? 0} maxLevel={template.levels ?? 5} />
            {!isBaseline && (
              <Typography variant="h4" mt={1} textAlign="center">
                <Box component="span" color="text.primary">
                  {Math.round(assessment.percentage ?? 0)}
                </Box>
                <Box component="span" color="text.disabled">
                  {formatMessage({ id: 'companyProfile.percentage' })}
                </Box>
              </Typography>
            )}
          </Stack>
          <Stack flex={1} minWidth={0} spacing={1}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="subtitle1" flex={1}>
                {[label, thresholdText].filter(Boolean).join(' ')}
              </Typography>
              {showSourceChip && (
                <Chip
                  variant="outlined"
                  label={formatMessage({
                    id: assessment.isScreened ? 'assessment.alert.aiScreened' : 'assessment.alert.selfReported',
                  })}
                />
              )}
            </Stack>
            <Typography variant="body1" color="text.secondary" component="div">
              <TruncateWithDialog title={label} text={description} />
            </Typography>
            <Typography variant="body2" color="text.secondary" alignSelf="flex-end">
              <DateDisplay value={assessment.updatedAt ?? assessment.createdAt} />
            </Typography>
          </Stack>
        </Stack>
        {showLocationAndRisks && <AssessmentLocationAndRisks provider={provider} riskIndices={riskIndices} />}
        {hasSectionRatings && <AssessmentSectionRatings assessment={assessment} />}
      </Stack>
    </Paper>
  )
}

export default AssessmentSummaryCard
